import React, { useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import ModalHeader from '../../components/ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';
import { formatCurrency } from '../../utils/money';
import { saveBlob } from '../../utils/saveFile';
import { invoiceListPath } from '../../services/invoiceList.service';
import {
  createInvoices, previewInvoiceCreation, printInvoices, INVOICE_CLASS_OPTIONS,
  type InvoiceClass, type InvoiceCreationRequest, type InvoiceCreationResult, type InvoiceCreationReview,
} from '../../services/accountingInvoiceSelection';

const BUTTON = 'rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed';
function message(error: unknown): string {
  if (isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (typeof detail?.message === 'string') return detail.message;
  }
  return error instanceof Error ? error.message : 'The invoice request failed.';
}

interface Props {
  invoiceRecnos: number[];
  onClose: () => void;
  onCreated: () => void;
}

export default function InvoiceCreationDialog({ invoiceRecnos, onClose, onCreated }: Props) {
  const [invoiceClass, setInvoiceClass] = useState<InvoiceClass>();
  const [review, setReview] = useState<InvoiceCreationReview | null>(null);
  const [reviewTick, setReviewTick] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [retry, setRetry] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<InvoiceCreationResult | null>(null);
  const [pdfUrl, setPdfUrl] = useState('');
  // Kept alongside the object URL: the URL drives the View / Print tab, the blob is what
  // Download hands to saveBlob so the save honours Show Save As.
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfError, setPdfError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const request = useRef<InvoiceCreationRequest | null>(null);
  const alive = useRef(true);
  const drag = useModalDrag(true);
  const selectionKey = invoiceRecnos.join(',');
  /** Did the plan leave any selected row out? Drives the "Included" column, which is
   *  hidden otherwise — see the note on the table. */
  const excluded = (review?.excludedRecnos.length ?? 0) > 0;

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);
  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setReview(null);
    request.current = null;
    void previewInvoiceCreation(selectionKey.split(',').map(Number), invoiceClass)
      .then((data) => { if (active) setReview(data); })
      .catch((e: unknown) => { if (active) setError(message(e)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [selectionKey, invoiceClass, reviewTick]);

  const loadPdf = async (created: InvoiceCreationResult) => {
    setPdfLoading(true);
    setPdfError('');
    try {
      const blob = await printInvoices(created.invoices.map((invoice) => invoice.recno));
      if (alive.current) {
        setPdfBlob(blob);
        setPdfUrl(URL.createObjectURL(blob));
      }
    } catch (e) {
      if (alive.current) setPdfError(message(e));
    } finally {
      if (alive.current) setPdfLoading(false);
    }
  };
  const create = async () => {
    if (!review?.canCreate || creating || loading || result) return;
    request.current ??= { invoiceRecnos: [...invoiceRecnos], invoiceClass: review.invoiceClass,
      snapshot: review.snapshot, requestId: crypto.randomUUID() };
    setCreating(true);
    setError('');
    try {
      const created = await createInvoices(request.current);
      if (!alive.current) return;
      setResult(created);
      setRetry(false);
      onCreated();
      void loadPdf(created);
    } catch (e) {
      if (!alive.current) return;
      if (isAxiosError(e) && e.response?.status === 409) {
        // The server rejected/rolled back this request. A fresh review is required.
        setReview(null);
        request.current = null;
        setRetry(false);
      } else {
        // Lost responses have an unknown outcome: retain the exact payload and UUID.
        setRetry(true);
      }
      setError(message(e));
    } finally {
      if (alive.current) setCreating(false);
    }
  };

  return <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="invoice-create-title">
    <div ref={drag.panelRef} style={drag.panelStyle} className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg bg-panel shadow-xl">
      <ModalHeader title={result ? 'Invoices Created' : 'Create Invoices'} titleId="invoice-create-title" onClose={creating ? undefined : onClose} dragHandleProps={drag.dragHandleProps} />
      <div className="overflow-auto p-5 space-y-4">
        {loading && <p role="status" className="text-sm text-muted">Checking invoice details, documents, EDI, driver rates, and margins…</p>}
        {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        {!result && review && <>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div><p className="font-semibold text-heading">{review.billtoName}</p><p className="text-sm text-muted">{review.invoiceCount} invoice{review.invoiceCount === 1 ? '' : 's'} · {formatCurrency(review.total)}</p></div>
            <label className="text-sm text-label">Invoice Type
              <select aria-label="Invoice Type" value={invoiceClass ?? review.invoiceClass} disabled={creating || retry} onChange={(e) => setInvoiceClass(e.target.value as InvoiceClass)} className="ml-2 rounded border border-theme-border-primary bg-panel p-2">
                {INVOICE_CLASS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
          </div>
          {[...review.holds, ...review.skipped].map((text) => <p key={text} role="alert" className="text-sm text-red-600 dark:text-red-400">{text}</p>)}
          {review.warnings.map((text) => <p key={text} className="text-sm text-amber-700 dark:text-amber-400">{text}</p>)}
          {excluded && <p className="text-sm text-amber-700 dark:text-amber-400">{review.excludedRecnos.length} charge rows do not qualify for this customer's batch rules and will remain uninvoiced.</p>}
          {/* "Included" is only shown when a row ISN'T. It can only ever say No when the
            * Bill To invoices each pro separately AND restricts which accessorials it
            * will take (invoiceseparately_items) — today Wal-Mart, Ross and Target, none of
            * which split on an accessorial run. So on every other review it was a column
            * of "Yes", which teaches people to skip the one place a "No" would matter.
            * The amber line above carries the count; this says WHICH rows. */}
          <table className="w-full text-left text-sm text-label">
            <thead className="bg-theme-bg-secondary"><tr><th className="p-2">PRO</th><th className="p-2">Acc #</th><th className="p-2">Container</th><th className="p-2 text-right">Amount</th>{excluded && <th className="p-2">Included</th>}</tr></thead>
            <tbody>{review.rows.map((row) => <tr key={row.invoiceRecno} className="border-b border-theme-border-primary"><td className="p-2">{row.pronumber}</td><td className="p-2">{row.accessorialNum}</td><td className="p-2">{row.container}</td><td className="p-2 text-right">{formatCurrency(row.total)}</td>{excluded && <td className="p-2" title={row.included ? undefined : "This Bill To invoices each pro separately and only takes certain accessorial charges. This one is left uninvoiced — it stays in the bucket."}>{row.included ? 'Yes' : 'No'}</td>}</tr>)}</tbody>
          </table>
        </>}
        {result && <>
          <p className="text-sm text-label">Created {result.invoices.length} invoice{result.invoices.length === 1 ? '' : 's'}.</p>
          {/* Every invoice links to its own Invoice List page, which is where View,
              Print, the three e-mail options and the Documents picker already live. The
              dashboard is a work queue: it hands the invoice over rather than growing a
              second copy of the invoice actions. NEW TAB so the queue is not lost.
              The link is SPELLED OUT rather than left as a bare invoice number — the
              number alone does not tell anyone that e-mailing lives behind it. */}
          <ul className="space-y-1 text-sm text-label">{result.invoices.map((invoice) => <li key={invoice.recno} className="flex flex-wrap items-baseline gap-2">
            <span className="font-medium tabular-nums">{invoice.invoiceNumber}</span>
            <span>· {formatCurrency(invoice.total)}</span>
            <a
              href={invoiceListPath({ invhistoryrecno: invoice.recno })}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-text underline underline-offset-2 hover:no-underline"
            >Open invoice →</a>
          </li>)}</ul>
          <p className="text-xs text-muted">
            To e-mail {result.invoices.length === 1 ? 'this invoice' : 'one of these'} to the
            customer, attach backup paperwork, or use the printer dialog, open
            {result.invoices.length === 1 ? ' it' : ' one'} above — those options live on
            the invoice itself. The buttons below just show the PDF that was archived.
          </p>
          {pdfLoading && <p role="status" className="text-sm text-muted">Loading invoice PDF…</p>}
          {pdfError && <p role="alert" className="text-sm text-red-600 dark:text-red-400">Invoices are saved. {pdfError}</p>}
          {pdfUrl && <div className="flex gap-3">
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className={BUTTON}>View / Print PDF</a>
            {/* A button, not an <a download>: the anchor always wrote straight to the
              * Downloads folder, ignoring Settings → Downloads → Show Save As. */}
            <button type="button" className={BUTTON} disabled={!pdfBlob}
              onClick={() => { if (pdfBlob) void saveBlob(pdfBlob, 'invoices.pdf'); }}>Download PDF</button>
          </div>}
          {pdfError && <button className={BUTTON} disabled={pdfLoading} onClick={() => void loadPdf(result)}>Retry PDF</button>}
        </>}
      </div>
      <div className="flex items-center justify-end gap-3 border-t border-theme-border-primary p-4">
        {/* Inspect before committing. This review is a summary — line items, holds in
          * full, documents and margin live on the Invoice List page — so anyone who
          * isn't sure can go read the detail instead of creating and unpicking it after.
          * Carries the SELECTION (invoicerecnos), not the masterpro, so it lands on the
          * accessorials in hand rather than everything the masterpro has ever billed.
          * New tab, and the modal closes: the selection is still on the board behind it,
          * so coming back to create is one click, and leaving a stale review parked over
          * the page it was about would only invite acting on it. */}
        {!result && <a
          href={invoiceListPath({ invoicerecnos: invoiceRecnos.join(',') })}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClose}
          className="mr-auto text-sm text-accent-text underline underline-offset-2 hover:no-underline"
        >Open in Invoice List</a>}
        {!result && !loading && !retry && <button type="button" disabled={creating} onClick={() => setReviewTick((tick) => tick + 1)} className="text-sm text-label">Refresh Review</button>}
        {!result && review && <button type="button" className={BUTTON} disabled={loading || creating || !review.canCreate} onClick={() => void create()}>{creating ? 'Creating…' : retry ? 'Retry Creation' : `Create ${review.invoiceCount} Invoice${review.invoiceCount === 1 ? '' : 's'}`}</button>}
        <button type="button" disabled={creating} onClick={onClose} className="text-sm text-label disabled:opacity-50">{result ? 'Done' : 'Cancel'}</button>
      </div>
    </div>
  </div>;
}
